import React, { useState, useEffect } from 'react';

const LeaveReport = () => {
  // Sample initial data
  const [leaveData, setLeaveData] = useState({
    employeeName: "John Doe",
    employeeId: "EMP-12345",
    department: "Engineering",
    leaveBalance: {
      sickLeave: 12,
      casualLeave: 8,
      earnedLeave: 18,
      compensatoryLeave: 2
    },
    leaveRequests: [
      {
        id: 1,
        type: "Sick Leave",
        startDate: "2023-05-15",
        endDate: "2023-05-17",
        days: 3,
        status: "Approved",
        reason: "Fever and cold"
      },
      {
        id: 2,
        type: "Casual Leave",
        startDate: "2023-06-01",
        endDate: "2023-06-02",
        days: 2,
        status: "Pending",
        reason: "Family function"
      },
      {
        id: 3,
        type: "Earned Leave",
        startDate: "2023-07-10",
        endDate: "2023-07-15",
        days: 6,
        status: "Rejected",
        reason: "Vacation"
      }
    ]
  });

  // State for filters
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all'
  });

  // State for showing leave application form
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [newLeaveRequest, setNewLeaveRequest] = useState({
    type: "Sick Leave",
    startDate: "",
    endDate: "",
    reason: ""
  });

  // Filter leave requests based on filters
  const filteredRequests = leaveData.leaveRequests.filter(request => {
    return (filters.status === 'all' || request.status === filters.status) &&
           (filters.type === 'all' || request.type === filters.type);
  });

  // Handle applying for new leave
  const handleApplyLeave = () => {
    if (newLeaveRequest.startDate && newLeaveRequest.endDate && newLeaveRequest.reason) {
      const start = new Date(newLeaveRequest.startDate);
      const end = new Date(newLeaveRequest.endDate);
      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      
      const newRequest = {
        id: leaveData.leaveRequests.length + 1,
        type: newLeaveRequest.type,
        startDate: newLeaveRequest.startDate,
        endDate: newLeaveRequest.endDate,
        days: days,
        status: "Pending",
        reason: newLeaveRequest.reason
      };
      
      setLeaveData({
        ...leaveData,
        leaveRequests: [newRequest, ...leaveData.leaveRequests]
      });
      
      setNewLeaveRequest({
        type: "Sick Leave",
        startDate: "",
        endDate: "",
        reason: ""
      });
      
      setShowLeaveForm(false);
    }
  };

  // Calculate leave statistics
  const leaveStats = {
    totalApproved: leaveData.leaveRequests.filter(req => req.status === 'Approved').length,
    totalPending: leaveData.leaveRequests.filter(req => req.status === 'Pending').length,
    totalRejected: leaveData.leaveRequests.filter(req => req.status === 'Rejected').length
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6 rounded-xl shadow-lg mb-6 animate-fade-in">
          <h1 className="text-3xl font-bold mb-2">Leave Management</h1>
          <p className="opacity-90">Track your leave requests and balance with transparency</p>
        </div>

        {/* Leave Balance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {Object.entries(leaveData.leaveBalance).map(([type, days], index) => (
            <div 
              key={type}
              className="bg-white rounded-xl shadow-md p-5 transform transition-all duration-300 hover:scale-105 hover:shadow-lg animate-fade-in-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <h3 className="text-lg font-semibold text-gray-700 capitalize">{type.replace(/([A-Z])/g, ' $1')}</h3>
              <p className="text-3xl font-bold mt-2 text-blue-600">{days} Days</p>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mt-4">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2.5 rounded-full" 
                  style={{ width: `${(days / 30) * 100}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>

        {/* Statistics and Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2 bg-white rounded-xl shadow-md p-6 animate-fade-in">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Leave Statistics</h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{leaveStats.totalApproved}</p>
                <p className="text-sm text-gray-600">Approved</p>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <p className="text-2xl font-bold text-yellow-600">{leaveStats.totalPending}</p>
                <p className="text-sm text-gray-600">Pending</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-2xl font-bold text-red-600">{leaveStats.totalRejected}</p>
                <p className="text-sm text-gray-600">Rejected</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 flex flex-col justify-center animate-fade-in">
            <button 
              onClick={() => setShowLeaveForm(!showLeaveForm)}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 transform hover:-translate-y-1"
            >
              Apply for Leave
            </button>
            <p className="text-sm text-gray-600 mt-3 text-center">Submit a new leave request for approval</p>
          </div>
        </div>

        {/* Leave Application Form */}
        {showLeaveForm && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-8 animate-slide-down">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Apply for Leave</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type</label>
                <select 
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={newLeaveRequest.type}
                  onChange={(e) => setNewLeaveRequest({...newLeaveRequest, type: e.target.value})}
                >
                  <option value="Sick Leave">Sick Leave</option>
                  <option value="Casual Leave">Casual Leave</option>
                  <option value="Earned Leave">Earned Leave</option>
                  <option value="Compensatory Leave">Compensatory Leave</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input 
                  type="date" 
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={newLeaveRequest.startDate}
                  onChange={(e) => setNewLeaveRequest({...newLeaveRequest, startDate: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input 
                  type="date" 
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={newLeaveRequest.endDate}
                  onChange={(e) => setNewLeaveRequest({...newLeaveRequest, endDate: e.target.value})}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <textarea 
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  rows="3"
                  value={newLeaveRequest.reason}
                  onChange={(e) => setNewLeaveRequest({...newLeaveRequest, reason: e.target.value})}
                ></textarea>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-4">
              <button 
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={() => setShowLeaveForm(false)}
              >
                Cancel
              </button>
              <button 
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                onClick={handleApplyLeave}
              >
                Submit
              </button>
            </div>
          </div>
        )}

        {/* Leave Requests Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden animate-fade-in">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Leave Requests</h2>
            <div className="flex flex-wrap gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Status</label>
                <select 
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                >
                  <option value="all">All Status</option>
                  <option value="Approved">Approved</option>
                  <option value="Pending">Pending</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Type</label>
                <select 
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={filters.type}
                  onChange={(e) => setFilters({...filters, type: e.target.value})}
                >
                  <option value="all">All Types</option>
                  <option value="Sick Leave">Sick Leave</option>
                  <option value="Casual Leave">Casual Leave</option>
                  <option value="Earned Leave">Earned Leave</option>
                  <option value="Compensatory Leave">Compensatory Leave</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{request.type}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{request.startDate} to {request.endDate}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{request.days}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{request.reason}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                        ${request.status === 'Approved' ? 'bg-green-100 text-green-800' : ''}
                        ${request.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : ''}
                        ${request.status === 'Rejected' ? 'bg-red-100 text-red-800' : ''}
                      `}>
                        {request.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Inline CSS for animations */}
      <style>
        {`
          @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes fade-in-up {
            from { 
              opacity: 0;
              transform: translateY(20px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes slide-down {
            from { 
              opacity: 0;
              transform: translateY(-20px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fade-in {
            animation: fade-in 0.5s ease-out;
          }
          .animate-fade-in-up {
            animation: fade-in-up 0.5s ease-out;
          }
          .animate-slide-down {
            animation: slide-down 0.3s ease-out;
          }
        `}
      </style>
    </div>
  );
};

export default LeaveReport;
